<script lang="ts">
	import { onMount } from "svelte";
	// Assumes @types/cloudflare-turnstile is installed and provides Turnstile.* types
	// Consider adding global declaration file (see explanation above) for better window.turnstile typing

	let {
		siteKey,
		onCallback, // Callback only needs the token
	}: {
		siteKey: string;
		onCallback: (token: string) => void;
	} = $props();

	// --- State ---
	let container = $state<HTMLDivElement | undefined>();
	let turnstileToken = $state(""); // For hidden input binding
	let widgetId: string | null = $state(null); // Store the widget ID

	// --- Render Parameters ---
	// $derived ensures this updates if siteKey prop changes reactively
	const renderParams = $derived<Turnstile.RenderParameters>({
		sitekey: siteKey,
		callback: (token: string) => {
			if (!isMounted) return; // Prevent updates after unmount
			console.info("Turnstile: Token received");
			turnstileToken = token; // Update state for hidden input
			onCallback(token); // Call parent callback
		},
		"expired-callback": () => {
			if (!isMounted) return;
			console.info("Turnstile: Token expired");
			widgetId = null;
			turnstileToken = "";
		},
		"error-callback": (error: string) => {
			if (!isMounted) return;
			console.error("Turnstile Error Callback:", error);
			widgetId = null;
			turnstileToken = "";
		},
		theme: "auto",
		tabindex: 5,
	});

	// --- Lifecycle & Logic ---
	let isMounted = $state(false);
	let cleanupPolling = () => {}; // Stores the function to clear interval if polling starts

	onMount(() => {
		isMounted = true;
		let localContainer = container; // Capture container ref on mount

		if (typeof window === "undefined" || !localContainer) {
			console.error("Turnstile: Invalid mount context.");
			return () => {
				isMounted = false;
			};
		}

		// --- Function to Render Turnstile ---
		const performRender = () => {
			// Use type assertion for window.turnstile until global types are set up
			const turnstileInstance = (window as Window & { turnstile?: Turnstile.Turnstile })
				.turnstile;

			if (!isMounted || !turnstileInstance || !localContainer) {
				console.warn("Turnstile: Conditions not met for rendering.");
				return;
			}
			try {
				console.info("Turnstile: Rendering widget...");
				const id = turnstileInstance.render(localContainer, renderParams);
				widgetId = id;
				console.info("Turnstile: Widget rendered with ID:", widgetId);
			} catch (err) {
				console.error("Turnstile: Error during explicit render:", err);
				widgetId = null;
			}
		};

		// --- Script Loading Logic ---
		const SCRIPT_ID = "cf-turnstile-script";
		const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

		const initialize = () => {
			// Use assertion again when checking/using
			if ((window as Window & { turnstile?: Turnstile.Turnstile }).turnstile) {
				performRender();
			} else {
				console.error("Turnstile: window.turnstile not found during initialization.");
			}
		};

		// 1. Check if ready
		if ((window as Window & { turnstile?: Turnstile.Turnstile }).turnstile) {
			console.info("Turnstile: API already available.");
			initialize();
		} else {
			// 2. Check if script tag exists (maybe loading)
			let scriptTag = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
			if (scriptTag) {
				console.info("Turnstile: Script tag found, polling for window.turnstile...");
				let attempts = 0;
				const intervalId = setInterval(() => {
					if (!isMounted) {
						clearInterval(intervalId);
						return;
					}
					if ((window as Window & { turnstile?: Turnstile.Turnstile }).turnstile) {
						clearInterval(intervalId);
						console.info("Turnstile: API ready after polling.");
						initialize();
					} else if (attempts++ > 60) {
						clearInterval(intervalId);
						console.error("Turnstile: Timed out waiting for existing script.");
					}
				}, 100);
				// Assign the specific cleanup function for this interval
				cleanupPolling = () => clearInterval(intervalId);
			} else {
				// 3. Load script
				console.info("Turnstile: Script tag not found, creating...");
				scriptTag = document.createElement("script");
				scriptTag.id = SCRIPT_ID;
				scriptTag.src = SCRIPT_SRC;
				scriptTag.async = true;
				scriptTag.defer = true;
				scriptTag.onload = () => {
					if (!isMounted) return;
					console.info("Turnstile: Script loaded via onload.");
					initialize(); // Initialize now
				};
				scriptTag.onerror = () => {
					if (!isMounted) return;
					console.error("Turnstile: Failed to load Turnstile script.");
				};
				document.head.appendChild(scriptTag);
				// No interval started in this path, so default empty cleanup is fine
				cleanupPolling = () => {};
			}
		}

		// --- Cleanup ---
		return () => {
			isMounted = false; // Mark as unmounted
			cleanupPolling(); // Execute cleanup (clears interval if it was started)
			console.info("Turnstile: Unmounting. Removing widget ID:", widgetId);
			const turnstileInstance = (window as Window & { turnstile?: Turnstile.Turnstile })
				.turnstile;
			if (widgetId && turnstileInstance) {
				try {
					turnstileInstance.remove(widgetId);
				} catch (e) {
					console.warn("Turnstile: Error removing widget during unmount:", e);
				}
			}
			widgetId = null; // Clear state
		};
	}); // End onMount
</script>

<div bind:this={container} class="cf-turnstile mb-4 w-full rounded" data-sitekey={siteKey}>
	{#if container}
		<input type="hidden" name="turnstileToken" id="turnstileToken" value={turnstileToken} />
	{/if}
	{#if !widgetId && isMounted}
		<span>Loading...</span>
	{/if}
</div>
